// BLOCKED_STATUSES: statuses bot protection answers with; the site is up, it just won't talk to us.
// BAD_ADDRESS_CODES: the address itself is wrong, so an AI lookup would be looking up nothing.
import { BAD_ADDRESS_CODES, BLOCKED_STATUSES, defaultBrandKit, launchPageSeconds, launchTemplates } from "@veyso/core";
import type { StartLaunchInput } from "../api.js";

/**
 * The new-launch-video wizard, as plain functions.
 *
 * Everything the page decides — what a failure means, when a step is done,
 * what will be sent and how the recap reads — lives here, where it can be
 * tested without a browser. The component holds state and draws it.
 */

export type WizardStep = "about" | "style" | "length";

export const wizardSteps: { key: WizardStep; label: string }[] = [
  { key: "about", label: "About" },
  { key: "style", label: "Style" },
  { key: "length", label: "Length" },
];

export type LookColors = { accent: string; accentAlt: string; background: string; text: string };

/**
 * The kit's colours as an upload can carry them.
 *
 * The kit accepts #abc, rgb() and names; the look the server takes wants
 * #rrggbb, so seeding from the kit as-is would send a start request that is
 * refused. Anything that cannot be written that way takes the default for its
 * role rather than guessing at a conversion.
 */
export function uploadColorsFrom(kit: LookColors): LookColors {
  const fix = (value: string, fallback: string): string => {
    const v = value.trim();
    if (/^#[0-9a-f]{6}$/i.test(v)) return v.toLowerCase();
    const short = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/i.exec(v);
    return short ? `#${short[1]}${short[1]}${short[2]}${short[2]}${short[3]}${short[3]}`.toLowerCase() : fallback;
  };
  const d = defaultBrandKit.colors;
  return {
    accent: fix(kit.accent, d.accent),
    accentAlt: fix(kit.accentAlt, d.accentAlt),
    background: fix(kit.background, d.background),
    text: fix(kit.text, d.text),
  };
}

export type Look =
  | { kind: "upload"; logoKey: string | null; logoUrl: string | null; colors: LookColors }
  | { kind: "kit" }
  | { kind: "none" };

/**
 * What the customer changed about a read site's look, for this film only.
 *
 * Each half on its own: somebody whose site has the right colours and a
 * blurry logo should be able to replace the logo and keep the colours, and
 * the recap says which is whose. Null in either half is the site's own.
 */
export type SiteLook = {
  logo: { key: string; url: string } | null;
  colors: LookColors | null;
};

/** Nothing changed: the site dresses its own film. */
export const siteOwnLook: SiteLook = { logo: null, colors: null };

export type Summary = { name: string; summary: string; sources: { title: string; url: string }[] };

export type SiteFailure = { host: string; message: string; offerAi: boolean };

/** What was typed, as the host people recognise; the text itself when it is not an address. */
export function hostOf(address: string): string {
  const trimmed = address.trim();
  try {
    const url = new URL(/^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`);
    return url.hostname.toLowerCase().replace(/^www\./, "") || trimmed;
  } catch {
    return trimmed;
  }
}

/**
 * Why a site could not be read, in words, and whether an AI lookup is worth offering.
 *
 * Read off the error's shape rather than `instanceof ApiError`, so this file
 * does not pull the browser's auth code into a test.
 */
export function siteFailure(error: unknown, address: string): SiteFailure {
  const host = hostOf(address);
  const { code, details } = (error ?? {}) as { code?: unknown; details?: unknown };
  const status = (details as { status?: unknown } | undefined)?.status;

  if (code === "site_error" && typeof status === "number") {
    return BLOCKED_STATUSES.has(status)
      ? { host, message: `${host} blocks automated readers, so we couldn't read it.`, offerAi: true }
      : { host, message: `${host} answered with an error (${status}), so we couldn't read it.`, offerAi: true };
  }
  if (code === "site_timeout") return { host, message: `${host} took too long to answer.`, offerAi: true };
  if (typeof code === "string" && BAD_ADDRESS_CODES.has(code)) {
    return { host, message: `Couldn't find ${host}. Check the address.`, offerAi: false };
  }
  return { host, message: `We couldn't read ${host}.`, offerAi: true };
}

/** A site that answered, but with a shell: nothing to make a film from. */
export function emptySite(address: string): SiteFailure {
  const host = hostOf(address);
  return { host, message: `We read ${host} but found almost no words on it.`, offerAi: true };
}

export function lookComplete(look: Look | null): boolean {
  return look !== null && (look.kind !== "upload" || look.logoKey !== null);
}

export type AboutState = {
  source: "website" | "topic";
  website: string;
  /** The site was read and its preview is showing. */
  siteRead: boolean;
  /** An AI summary, as shown and edited. Present means the AI path is the one in use. */
  summary: Summary | null;
  look: Look | null;
  /** Changes to the read site's logo and colours. Only used on the plain-site path. */
  siteLook: SiteLook;
  name: string;
  topic: string;
};

/** The floor the API holds a topic to. */
const MIN_TOPIC = 20;

export function canLeaveAbout(about: AboutState): boolean {
  if (about.source === "topic") return about.name.trim().length > 0 && about.topic.trim().length >= MIN_TOPIC;
  if (about.summary) {
    return (
      about.summary.name.trim().length > 0 &&
      about.summary.summary.trim().length >= MIN_TOPIC &&
      lookComplete(about.look)
    );
  }
  return about.siteRead;
}

type Choice = { templateId: string | null; seconds: number | null };

function wireLook(look: Look | null): Extract<StartLaunchInput, { topic: string }>["look"] {
  if (!look) return undefined;
  if (look.kind === "upload") return { kind: "upload", logoKey: look.logoKey ?? "", colors: look.colors };
  return { kind: look.kind };
}

export function startRequest(about: AboutState, choice: Choice): StartLaunchInput {
  const extra = {
    ...(choice.templateId ? { templateId: choice.templateId } : {}),
    ...(choice.seconds ? { seconds: choice.seconds } : {}),
  };
  if (about.source === "topic") {
    return { name: about.name.trim(), topic: about.topic.trim(), source: "described", ...extra };
  }
  if (about.summary) {
    const look = wireLook(about.look);
    return {
      name: about.summary.name.trim(),
      topic: about.summary.summary.trim(),
      website: about.website.trim(),
      source: "ai",
      ...(look ? { look } : {}),
      ...extra,
    };
  }
  // Only what was changed is sent, so a film nobody restyled is asked for
  // exactly as before and the server reads everything off the site.
  const brand = {
    ...(about.siteLook.logo ? { logoKey: about.siteLook.logo.key } : {}),
    ...(about.siteLook.colors ? { colors: about.siteLook.colors } : {}),
  };
  return { website: about.website.trim(), ...(Object.keys(brand).length > 0 ? { brand } : {}), ...extra };
}

function lookLine(about: AboutState): string {
  if (about.source === "topic") return "Your brand kit";
  if (!about.summary) {
    const { logo, colors } = about.siteLook;
    if (logo && colors) return "Your logo · your colours";
    if (logo) return "Your logo · the site's colours";
    if (colors) return "The site's logo · your colours";
    return "The site's logo and colours";
  }
  switch (about.look?.kind) {
    case "upload":
      return `Uploaded logo · ${about.look.colors.accent}`;
    case "none":
      return "No logo · the name in your brand kit's colours";
    default:
      return "Your brand kit";
  }
}

export function recapLines(about: AboutState, choice: Choice): { step: WizardStep; label: string; value: string }[] {
  const what =
    about.source === "topic"
      ? `${about.name.trim()} · described by you`
      : about.summary
        ? `${about.summary.name.trim()} · summary written by AI, checked by you`
        : `${hostOf(about.website)} · read from the site`;
  const template = launchTemplates.find((one) => one.id === choice.templateId) ?? null;
  const length = choice.seconds
    ? `${choice.seconds} seconds`
    : template?.seconds
      ? `${template.seconds} seconds, the style's own`
      : `The style's own (usually ${launchPageSeconds} seconds)`;
  return [
    { step: "about", label: "About", value: what },
    { step: "about", label: "Look", value: lookLine(about) },
    { step: "style", label: "Style", value: template?.name ?? "Whichever suits it" },
    { step: "length", label: "Length", value: length },
  ];
}
